import type { BhmrPackageInfo, SaleOrderLine, SaleOrderSourceLine, ScannedSerialRow } from "../types";
import { findOrderLineForRow } from "./selectors";

/*
 * Bảo hành mở rộng (BHMR) tự gắn trên phiếu xuất bán, dựa vào dòng gói của đơn bán:
 * - ATTRIBUTE38 dòng gói = máy trên cùng đơn "ITEM_CODE*số suất, ...": thẻ ghép với máy đã quét trên phiếu
 *   có mã nằm trong danh sách, mỗi mã tối đa số suất.
 * - ATTRIBUTE39 dòng gói = serial máy ngoài đơn: thẻ trống đầu tiên nhận serial đầu tiên, chỉ đọc.
 * Mỗi máy chỉ gắn một thẻ, giống màn đơn (mỗi máy chỉ gán tổng cộng 1 suất gói).
 * Gói không ghi phân bổ (gói chưa chia): không tự gắn (không đoán máy theo mã / tên), người dùng chọn tay
 * trong các máy trên phiếu.
 */

const norm = (value?: string | null) => (value ?? "").trim().toUpperCase();
const splitList = (raw?: string | null) =>
  (raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Tách ATTRIBUTE38 / ATTRIBUTE39 của dòng gói. Cùng quy tắc fromStoredWarranty ở màn đơn.
 * Mẩu 38 không đúng dạng "MÃ*n" (n > 0) bị bỏ qua; mã trùng (không phân biệt hoa thường) thì cộng suất.
 */
export const parseBhmrPackage = (attribute38?: string | null, attribute39?: string | null): BhmrPackageInfo => {
  const slots = new Map<string, { itemCode: string; slots: number }>();
  for (const token of splitList(attribute38)) {
    const star = token.lastIndexOf("*");
    if (star <= 0) continue;
    const itemCode = token.slice(0, star).trim();
    const count = parseInt(token.slice(star + 1).trim(), 10);
    if (!itemCode || !(count > 0)) continue;
    const entry = slots.get(norm(itemCode));
    if (entry) entry.slots += count;
    else slots.set(norm(itemCode), { itemCode, slots: count });
  }
  const seen = new Set<string>();
  const externalSerials = splitList(attribute39).filter((serial) => {
    if (seen.has(norm(serial))) return false;
    seen.add(norm(serial));
    return true;
  });
  return { slots: Array.from(slots.values()), externalSerials };
};

/**
 * Dòng đơn là gói BHMR: chỉ theo cờ ATTRIBUTE57 backend trả (isBhmrPackage), không theo tiền tố mã
 * (nhiều mã tiền tố BH không phải gói BHMR).
 */
export const isBhmrPackageLine = (line: SaleOrderSourceLine) => line.isBhmrPackage === true;

/**
 * Phân bổ của một mã gói trên đơn, đọc lần lượt:
 * 1. Định dạng mới trên dòng gói: ATTRIBUTE38 "mã máy*số suất", ATTRIBUTE39 serial máy ngoài đơn.
 * 2. Định dạng cũ (đơn develop lưu trước 01/10/2026): mã gói ghi trên ATTRIBUTE38 dòng máy, mỗi suất một lần;
 *    ATTRIBUTE38 dòng gói là serial máy cũ.
 * 3. Không có gì: rỗng, thẻ không tự gắn, chọn tay máy trên phiếu.
 */
export const resolveBhmrPackage = (
  packageLine: SaleOrderSourceLine,
  lines: SaleOrderSourceLine[],
): BhmrPackageInfo => {
  const parsed = parseBhmrPackage(packageLine.attribute38, packageLine.attribute39);
  if (parsed.slots.length || parsed.externalSerials.length) return parsed;

  const packageKey = norm(packageLine.itemCode);
  const slots = new Map<string, { itemCode: string; slots: number }>();
  lines.forEach((line) => {
    const itemCode = (line.itemCode ?? "").trim();
    if (!itemCode || isBhmrPackageLine(line)) return;
    const count = splitList(line.attribute38).filter((code) => norm(code) === packageKey).length;
    if (!count) return;
    const entry = slots.get(norm(itemCode));
    if (entry) entry.slots += count;
    else slots.set(norm(itemCode), { itemCode, slots: count });
  });
  const oldSerials = splitList(packageLine.attribute38)
    .filter((token) => !token.includes("*"))
    .join(", ");
  return { slots: Array.from(slots.values()), externalSerials: parseBhmrPackage(null, oldSerials).externalSerials };
};

/** Mã hàng không phải gói trên đơn (mã áp dụng của gói không ghi máy trong đơn), tính một lần cho mỗi mảng orderLines */
const deviceTargetsByLines = new WeakMap<SaleOrderLine[], string[]>();
const deviceTargetsOf = (orderLines: SaleOrderLine[]) => {
  let targets = deviceTargetsByLines.get(orderLines);
  if (!targets) {
    targets = orderLines.filter((line) => !line.bhmr && line.itemCode).map((line) => line.itemCode as string);
    deviceTargetsByLines.set(orderLines, targets);
  }
  return targets;
};

/**
 * Serial thuộc mã gói BHMR trên đơn là thẻ. Gói không serial (1 dòng SL N) không ghép 1-1 được nên bỏ qua.
 * Mã áp dụng (chặt, rỗng nghĩa là không máy nào trên phiếu): mã máy trong ATTRIBUTE38; gói không ghi máy trong
 * đơn (chưa phân bổ, hoặc chỉ có serial máy ngoài đơn) thì mọi mã hàng không phải gói trên đơn, để chọn tay thẻ dư.
 */
export const withBhmrCardFlags = (row: ScannedSerialRow, orderLines: SaleOrderLine[]): ScannedSerialRow => {
  const pkg = findOrderLineForRow(orderLines, row)?.bhmr;
  if (!pkg || row.isNoSerial) return row;
  const targets = pkg.slots.length ? pkg.slots.map((slot) => slot.itemCode) : deviceTargetsOf(orderLines).slice();
  return { ...row, isWarrantyCard: true, warrantyTargetItemCodes: targets, warrantyTargetsStrict: true };
};

const isDevice = (row: ScannedSerialRow) =>
  !row.isWarrantyCard && !row.isNoSerial && !row.hasActiveExtWarranty && !!norm(row.serial);
const isFreeCard = (row: ScannedSerialRow) =>
  !!row.isWarrantyCard && !norm(row.linkedDeviceSerial) && !row.linkReadOnly && !row.autoLinkOff;

interface BhmrScope {
  hasPackages: boolean;
  /** Mã máy (viết hoa) có trong ATTRIBUTE38 của ít nhất một gói */
  slotCodes: Set<string>;
}
const scopeByLines = new WeakMap<SaleOrderLine[], BhmrScope>();
const bhmrScopeOf = (orderLines: SaleOrderLine[]): BhmrScope => {
  let scope = scopeByLines.get(orderLines);
  if (!scope) {
    const packages = orderLines.filter((line) => line.bhmr);
    scope = {
      hasPackages: packages.length > 0,
      slotCodes: new Set(
        packages.flatMap((line) => (line.bhmr as BhmrPackageInfo).slots.map((slot) => norm(slot.itemCode))),
      ),
    };
    scopeByLines.set(orderLines, scope);
  }
  return scope;
};

/**
 * Dòng mới có thể đổi kết quả tự gắn không: thẻ, hoặc máy được gói nhắc tới (ATTRIBUTE38).
 * Dòng khác (phụ kiện, tem...) thì bỏ qua applyBhmrAutoLink được, vì rows cũ đã là điểm dừng của hàm đó.
 */
export const affectsBhmrAutoLink = (row: ScannedSerialRow, orderLines: SaleOrderLine[]) => {
  const scope = bhmrScopeOf(orderLines);
  if (!scope.hasPackages) return false;
  if (row.isWarrantyCard) return true;
  if (!isDevice(row)) return false;
  return scope.slotCodes.has(norm(row.itemCode));
};

/**
 * Lấp thẻ trống theo dòng gói: serial máy ngoài đơn trước, sau đó máy trên phiếu còn suất (ATTRIBUTE38).
 * Gói không ghi máy trong đơn (chưa phân bổ, hoặc chỉ có serial máy ngoài đơn mà thẻ nhiều hơn): thẻ còn trống
 * để chọn tay, không đoán máy theo mã / tên.
 * Không gỡ liên kết có sẵn (kể cả gắn tay); liên kết tay vẫn trừ vào suất. Thẻ theo thứ tự quét.
 * Chạy lại không đổi kết quả; không có gì đổi thì trả nguyên `rows`.
 */
export const applyBhmrAutoLink = (rows: ScannedSerialRow[], orderLines: SaleOrderLine[]): ScannedSerialRow[] => {
  const packages = orderLines.filter((line) => line.bhmr);
  if (!packages.length) return rows;

  const next = rows.slice();
  let changed = false;
  // Máy (hoặc serial ngoài đơn) đã nằm trên một thẻ bất kỳ
  const held = new Set(
    rows
      .filter((row) => row.isWarrantyCard)
      .map((row) => norm(row.linkedDeviceSerial))
      .filter(Boolean),
  );
  const deviceCode = new Map(rows.filter(isDevice).map((row) => [norm(row.serial), norm(row.itemCode)] as const));
  const link = (index: number, serial: string, readOnly: boolean) => {
    next[index] = { ...next[index], linkedDeviceSerial: serial, linkReadOnly: readOnly };
    held.add(norm(serial));
    changed = true;
  };
  const cardsOf = (line: SaleOrderLine) => {
    const packageCode = norm(line.itemCode);
    const cards: number[] = [];
    next.forEach((row, index) => {
      if (row.isWarrantyCard && norm(row.itemCode) === packageCode) cards.push(index);
    });
    return cards;
  };

  for (const line of packages) {
    const { slots, externalSerials } = line.bhmr as BhmrPackageInfo;
    const cards = cardsOf(line);
    if (!cards.length) continue;

    // Máy ngoài đơn: thẻ trống theo thứ tự trên phiếu, chỉ đọc
    const pending = externalSerials.filter((serial) => !held.has(norm(serial)));
    for (const index of cards) {
      if (!pending.length) break;
      if (isFreeCard(next[index])) link(index, pending.shift() as string, true);
    }

    // Máy trên phiếu có mã trong ATTRIBUTE38: suất còn lại sau khi trừ các thẻ gói này đang giữ máy
    const left = new Map(slots.map((slot) => [norm(slot.itemCode), slot.slots] as const));
    if (!left.size) continue;
    cards.forEach((index) => {
      if (next[index].linkReadOnly) return;
      const code = deviceCode.get(norm(next[index].linkedDeviceSerial));
      if (code && left.has(code)) left.set(code, (left.get(code) ?? 0) - 1);
    });
    const freeDevices = rows.filter(
      (row) => isDevice(row) && left.has(norm(row.itemCode)) && !held.has(norm(row.serial)),
    );
    for (const index of cards) {
      if (!isFreeCard(next[index])) continue;
      const position = freeDevices.findIndex((device) => (left.get(norm(device.itemCode)) ?? 0) > 0);
      if (position < 0) break;
      const [device] = freeDevices.splice(position, 1);
      left.set(norm(device.itemCode), (left.get(norm(device.itemCode)) ?? 0) - 1);
      // Giữ đúng chính tả serial: REMOVE_ROWS và LINK_DEVICE so khớp bằng trim
      link(index, (device.serial ?? "").trim(), false);
    }
  }

  return changed ? next : rows;
};
